import React, { useMemo, Suspense, lazy, useState, useEffect } from 'react';
import {
    Box, Typography, List, ListItem, Chip, Button, Paper, IconButton, TextField, InputAdornment, Skeleton
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import TicketForm from './tools/custom/custom_table';
import { StyledDialog, StyledDialogTitle } from '../../main_components/material_table/styles/general_form_styles';
import type { SUPPORTS } from "../interface/interface";

const COMMENTS_SUPPORT = lazy(() => import('./tools/custom/comments'));

const severityPriority: Record<string, number> = {
    'Alta': 1, 'Media': 2, 'Baja': 3
};

const severityColors: Record<string, { bg: string; color: string }> = {
    'Alta': { bg: '#FEE2E2', color: '#B91C1C' },
    'Media': { bg: '#FEF9C3', color: '#92400E' },
    'Baja': { bg: '#D1FAE5', color: '#065F46' }
};

const statusColors: Record<string, { bg: string; color: string; label: string }> = {
    'OPEN': { bg: '#E0E7FF', color: '#3730A3', label: 'ABIERTO' },
    'CLOSE': { bg: '#E5E7EB', color: '#6B7280', label: 'FINALIZADO' },
    'PROCESS': { bg: '#e8e4f2', color: 'black', label: 'EN PROCESO' },
    'VALIDATION': { bg: '#FEF9C3', color: '#92400E', label: 'EN VALIDACIÓN' },
    'REVIEW': { bg: '#FDE68A', color: '#92400E', label: 'EN REVISIÓN' },

};

interface RecentTicketsProps {
    tickets: SUPPORTS[];
    fetchSupports: () => Promise<SUPPORTS[]>;
    flagModal: boolean;
    onUpdateTicket: (updated: SUPPORTS) => void;

}

const RecentTickets: React.FC<RecentTicketsProps> = ({ tickets, flagModal, onUpdateTicket }) => {
    const [openForm, setOpenForm] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState<null | SUPPORTS>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => clearTimeout(handler);
    }, [searchTerm]);

    const handleClose = () => {
        setOpenForm(false);
        setSelectedTicket(null);
    };

    const recentTickets = useMemo(() => {
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

        return [...tickets]
            .filter(ticket => {
                const updatedDate = new Date(ticket.last_update);
                return updatedDate >= oneMonthAgo;
            })
            .filter(ticket =>
                ticket.folio?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
                ticket.subject?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
            .sort((a, b) => {
                const aHasUpdate = !!a.last_update;
                const bHasUpdate = !!b.last_update;

                if (aHasUpdate && !bHasUpdate) return -1;
                if (!aHasUpdate && bHasUpdate) return 1;

                if (aHasUpdate && bHasUpdate) {
                    const dateDiff = new Date(b.last_update!).getTime() - new Date(a.last_update!).getTime();
                    if (dateDiff !== 0) return dateDiff;
                }

                const aPriority = severityPriority[a.severity || ''] ?? 4;
                const bPriority = severityPriority[b.severity || ''] ?? 4;
                return aPriority - bPriority;
            });
    }, [tickets, debouncedSearchTerm]);

    return (
        <>
            <StyledDialog open={openForm} maxWidth="lg" fullWidth>
                <StyledDialogTitle>
                    {selectedTicket ? (
                        <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                            <Chip label={selectedTicket?.folio} size="small" sx={{ mr: 1, backgroundColor: '#E0E7FF', color: '#3730A3' }} />
                            <span style={{ color: 'white' }}>{selectedTicket?.subject}</span>
                        </Typography>
                    ) : 'ALTA DE TICKET'}
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </StyledDialogTitle>

                {selectedTicket ? (
                    <Suspense fallback={<Typography sx={{ p: 2 }}>Cargando comentarios...</Typography>}>
                        <COMMENTS_SUPPORT tickets={selectedTicket} onTicketUpdate={onUpdateTicket} />
                    </Suspense>
                ) : (
                    <TicketForm handleClose={handleClose} onTicketUpdate={onUpdateTicket} />
                )}
            </StyledDialog>

            <Paper elevation={3} sx={{ p: 2, borderRadius: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                        <ChatBubbleOutlineIcon fontSize="small" />
                        Historial
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        sx={{ backgroundColor: '#0472e9', textTransform: 'none', borderRadius: 2 }}
                        onClick={() => {
                            setSelectedTicket(null);
                            setOpenForm(true);
                        }}
                    >
                        Abrir Nuevo Ticket
                    </Button>
                </Box>

                {/* Buscador */}
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        placeholder="Buscar por folio o asunto..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => setSearchTerm('')}
                                        edge="end"
                                        sx={{ color: '#9CA3AF' }}
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: {
                                height: 36,
                                fontSize: 14,
                                borderRadius: '12px',
                                backgroundColor: '#F3F4F6',
                                color: '#111827',
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                paddingRight: 1,
                            },
                            '& input::placeholder': {
                                fontSize: '14px',
                                color: '#9CA3AF',
                            },
                        }}
                    />
                </Box>

                {/* Lista */}
                <Box sx={{ height: 300, overflowY: 'auto' }}>
                    {flagModal ? (
                        <List>
                            {[1, 2, 3, 4, 5].map((i) => (
                                <ListItem key={i} divider>
                                    <Skeleton variant="rectangular" height={40} width="100%" />
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <List>
                            {recentTickets.map((ticket) => {
                                const severity = ticket.severity || 'Desconocida';
                                const severityStyle = severityColors[severity] || { bg: '#E5E7EB', color: '#374151' };
                                const status = ticket.status || 'OPEN';
                                const statusStyle = statusColors[status] || { bg: '#F3F4F6', color: '#6B7280', label: 'DESCONOCIDO' };

                                return (
                                    <ListItem
                                        key={ticket.ticket}
                                        divider
                                        sx={{
                                            py: 2,
                                            px: 2,
                                            backgroundColor: ticket.recurrent_incidence ? '#FFF7ED' : 'transparent',
                                            borderLeft: ticket.recurrent_incidence ? '4px solid #FB923C' : 'none',
                                            transition: 'background-color 0.3s ease'
                                        }}
                                    >
                                        <Box display="flex" flexDirection="column" width="100%">
                                            <Box display="flex" justifyContent="space-between" width="100%" alignItems="center" gap={2}>
                                                <Box sx={{ textAlign: "justify", textTransform: "uppercase" }}>
                                                    <Typography variant="subtitle2">
                                                        {`${ticket?.folio || ticket?.ticket.slice(0, 8)} - ${ticket?.subject}`}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Chip
                                                        label={severity}
                                                        sx={{
                                                            backgroundColor: severityStyle.bg,
                                                            color: severityStyle.color,
                                                            borderRadius: 2,
                                                            width: 80
                                                        }}
                                                    />
                                                    <Chip
                                                        label={statusStyle.label}
                                                        sx={{
                                                            backgroundColor: statusStyle.bg,
                                                            color: statusStyle.color,
                                                            borderRadius: 2,
                                                            width: 120
                                                        }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedTicket(ticket);
                                                            setOpenForm(true);
                                                        }}
                                                        title="Contestar ticket"
                                                    >
                                                        <ChatBubbleOutlineIcon fontSize="small" sx={{ color: '#7C3AED' }} />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} sx={{ textTransform: "uppercase" }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Creado por: {ticket.user_name || 'Desconocido'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Actualizado: {new Date(ticket.last_update).toLocaleDateString()}
                                                </Typography>

                                            </Box>
                                        </Box>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </Box>
            </Paper>
        </>
    );
};

export default RecentTickets;
